import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  Row,
  Col,
  Tag,
  Button,
  Breadcrumb,
  Table,
  Timeline,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Descriptions,
  Typography,
  Spin,
  message,
  Divider,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloseOutlined,
  UserAddOutlined,
  CarOutlined,
  BarcodeOutlined,
  CheckCircleFilled,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, Link } from "react-router-dom";
import dayjs from "dayjs";
import { selectAuthInfo } from "../../Auth/authSelectors";
import { accessChecker } from "../../../lib/functions";
import {
  fetchDeliveryJob,
  advanceJob,
  assignJob,
  markDelivered,
  markComplete,
  cancelDeliveryJob,
  fetchDeliveryUsers,
} from "../deliveryThunks";
import {
  selectDeliveryJobDetail,
  selectDeliveryLoading,
  selectDeliveryUsers,
  selectDeliveryUsersLoading,
} from "../deliverySelectors";
import { clearJobDetail } from "../deliverySlice";

const { Title, Text } = Typography;
const { Option } = Select;

const STATUS_LABELS = {
  picking: "Picking",
  picking_verification: "Picking Verification",
  tatmeen_verification: "Tatmeen Verification",
  packing: "Packing",
  on_transit: "On Transit",
  delivered: "Delivered",
  completed: "Completed",
  cancelled: "Cancelled",
};

const STATUS_COLORS = {
  picking: "blue",
  picking_verification: "purple",
  tatmeen_verification: "cyan",
  packing: "blue",
  on_transit: "geekblue",
  delivered: "green",
  completed: "success",
  cancelled: "red",
};

const ADVANCE_BUTTON_LABELS = {
  picking: "Mark Picked",
  picking_verification: "Complete Picking Verification",
  tatmeen_verification: "Complete Verification",
  packing: "Mark Packed",
  on_transit: null, // handled by markDelivered
};

const getUserRoles = (authInfo) => {
  const roleStr = authInfo?.userInfo?.user_role_names || "";
  return roleStr.split(",").map((r) => r.trim().toLowerCase());
};

const canAdvanceStatus = (status, roles) => {
  const isAdmin = roles.some(
    (r) => r.includes("admin") || r.includes("delivery_admin")
  );
  if (isAdmin) return true;

  const roleMap = {
    picking: (r) => r.includes("picker"),
    picking_verification: (r) => r.includes("picking_verifier"),
    tatmeen_verification: (r) => r.includes("packing_verifier"),
    packing: (r) => r.includes("packer"),
    on_transit: (r) => r.includes("delivery_agent"),
  };

  const check = roleMap[status];
  return check ? roles.some(check) : false;
};

const DeliveryJobDetail = ({ authInfo: authInfoProp }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const authInfoFromStore = useSelector(selectAuthInfo);
  const authInfo = authInfoProp || authInfoFromStore;

  const jobDetail = useSelector(selectDeliveryJobDetail);
  const loading = useSelector(selectDeliveryLoading);
  const deliveryUsers = useSelector(selectDeliveryUsers);
  const usersLoading = useSelector(selectDeliveryUsersLoading);

  const hasWriteAccess = accessChecker("delivery_jobs_manage");
  const roles = getUserRoles(authInfo);
  const isAdmin = roles.some(
    (r) => r.includes("admin") || r.includes("delivery_admin")
  );

  // Load delivery users for assign modal
  useEffect(() => {
    dispatch(fetchDeliveryUsers());
  }, [dispatch]);

  // Modal states
  const [deliverModal, setDeliverModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [assignModal, setAssignModal] = useState(false);
  const [scanModal, setScanModal] = useState(false);
  const [scannedIds, setScannedIds] = useState(new Set());
  const [scanInput, setScanInput] = useState("");
  const scanInputRef = useRef(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [deliverForm] = Form.useForm();
  const [cancelForm] = Form.useForm();
  const [assignForm] = Form.useForm();

  useEffect(() => {
    if (id) {
      dispatch(fetchDeliveryJob(id));
    }
    return () => {
      dispatch(clearJobDetail());
    };
  }, [id]);

  const handleScan = (value) => {
    const q = value.trim().toLowerCase();
    if (!q) return;
    const items = jobDetail?.items || [];
    const matched = items.find(
      (it) =>
        (it.item_barcode && it.item_barcode.toLowerCase() === q) ||
        (it.item_code && it.item_code.toLowerCase() === q) ||
        (it.item_name && it.item_name.toLowerCase() === q)
    );
    if (matched) {
      setScannedIds((prev) => new Set([...prev, matched.item_id]));
      message.success(`✓ ${matched.item_name}`);
    } else {
      message.warning("Item not found in this invoice");
    }
    setScanInput("");
    setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  const handleAdvance = async () => {
    try {
      setActionLoading(true);
      await dispatch(advanceJob({ id, notes: "" })).unwrap();
      message.success("Job status advanced");
    } catch (err) {
      message.error(err?.message || err?.error || "Failed to advance job");
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkDelivered = async () => {
    try {
      const values = await deliverForm.validateFields();
      setActionLoading(true);
      await dispatch(markDelivered({ id, notes: values.notes || "" })).unwrap();
      message.success("Marked as delivered");
      setDeliverModal(false);
      deliverForm.resetFields();
    } catch (err) {
      message.error(err?.message || err?.error || "Failed to mark delivered");
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async () => {
    try {
      setActionLoading(true);
      await dispatch(markComplete(id)).unwrap();
      message.success("Job completed");
    } catch (err) {
      message.error(err?.message || err?.error || "Failed to complete job");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    try {
      const values = await cancelForm.validateFields();
      setActionLoading(true);
      await dispatch(
        cancelDeliveryJob({ id, notes: values.notes || "" })
      ).unwrap();
      message.success("Job cancelled");
      setCancelModal(false);
      cancelForm.resetFields();
    } catch (err) {
      message.error(err?.message || err?.error || "Failed to cancel job");
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssign = async () => {
    try {
      const values = await assignForm.validateFields();
      setActionLoading(true);
      await dispatch(
        assignJob({ id, step: values.step, user_id: values.user_id })
      ).unwrap();
      message.success("User assigned");
      setAssignModal(false);
      assignForm.resetFields();
    } catch (err) {
      message.error(err?.message || err?.error || "Failed to assign user");
    } finally {
      setActionLoading(false);
    }
  };

  const currentStatus = jobDetail?.status;
  const canAdvance = hasWriteAccess && canAdvanceStatus(currentStatus, roles);
  const advanceLabel = ADVANCE_BUTTON_LABELS[currentStatus];

  const itemColumns = [
    {
      title: "Item",
      dataIndex: "item_name",
      key: "item_name",
      render: (val, record) => val || record.product_name || "-",
    },
    {
      title: "Qty",
      dataIndex: "item_qty",
      key: "item_qty",
      align: "right",
    },
    {
      title: "Unit Price",
      dataIndex: "item_price",
      key: "item_price",
      align: "right",
      render: (val) => (val != null ? Number(val).toFixed(2) : "-"),
    },
    {
      title: "Total",
      dataIndex: "item_total",
      key: "item_total",
      align: "right",
      render: (val, record) => {
        const total = val != null ? val : (record.item_qty || 0) * (record.item_price || 0);
        return Number(total).toFixed(2);
      },
    },
  ];

  const timelineItems = (jobDetail?.status_history || []).map((h) => ({
    label: h.changed_at ? dayjs(h.changed_at).format("DD MMM YYYY HH:mm") : "",
    children: (
      <Space direction="vertical" size={0}>
        <Tag color={STATUS_COLORS[h.to_status] || "default"}>
          {STATUS_LABELS[h.to_status] || h.to_status}
        </Tag>
        {h.notes && <Text type="secondary" style={{ fontSize: 12 }}>{h.notes}</Text>}
        {(h.changed_by_name || h.changed_by) && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            by {h.changed_by_name || h.changed_by}
          </Text>
        )}
      </Space>
    ),
  }));

  if (!jobDetail && loading) {
    return (
      <div style={{ textAlign: "center", padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!jobDetail && !loading) {
    return (
      <div style={{ padding: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/delivery/jobs")}>
          Back to Jobs
        </Button>
        <div style={{ marginTop: 24, textAlign: "center", color: "#999" }}>
          Job not found
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "0 4px" }}>
      {/* Breadcrumb */}
      <Breadcrumb
        style={{ marginBottom: 12 }}
        items={[
          {
            title: (
              <span
                style={{ cursor: "pointer" }}
                onClick={() => navigate("/delivery/dashboard")}
              >
                Delivery
              </span>
            ),
          },
          {
            title: (
              <span
                style={{ cursor: "pointer" }}
                onClick={() => navigate("/delivery/jobs")}
              >
                Jobs
              </span>
            ),
          },
          {
            title: `#${jobDetail?.voucher_no || jobDetail?.voucher_number || id}`,
          },
        ]}
      />

      <Spin spinning={loading}>
        {/* Header */}
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Space>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate("/delivery/jobs")}
              />
              <Title level={4} style={{ margin: 0 }}>
                {jobDetail?.voucher_no ||
                  jobDetail?.voucher_number ||
                  `Job #${id}`}
              </Title>
              <Tag
                color={STATUS_COLORS[currentStatus] || "default"}
                style={{ fontSize: 14, padding: "2px 10px" }}
              >
                {STATUS_LABELS[currentStatus] || currentStatus || "-"}
              </Tag>
            </Space>
          </Col>
          <Col>
            <Space wrap>
              {/* Advance button */}
              {canAdvance && advanceLabel && (
                <Button
                  type="primary"
                  loading={actionLoading}
                  onClick={handleAdvance}
                >
                  {advanceLabel}
                </Button>
              )}
              {/* Mark Delivered (on_transit) */}
              {canAdvance && currentStatus === "on_transit" && (
                <Button
                  type="primary"
                  icon={<CarOutlined />}
                  onClick={() => setDeliverModal(true)}
                >
                  Mark Delivered
                </Button>
              )}
              {/* Complete / Cancel for admin */}
              {isAdmin && currentStatus === "delivered" && (
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  loading={actionLoading}
                  onClick={handleComplete}
                >
                  Complete
                </Button>
              )}
              {isAdmin &&
                currentStatus !== "completed" &&
                currentStatus !== "cancelled" && (
                  <Button
                    danger
                    icon={<CloseOutlined />}
                    onClick={() => setCancelModal(true)}
                  >
                    Cancel
                  </Button>
                )}
              {/* Scan Items */}
              {["picking", "picking_verification", "packing"].includes(currentStatus) &&
                jobDetail?.items?.length > 0 && (
                  <Button
                    icon={<BarcodeOutlined />}
                    onClick={() => { setScanModal(true); setTimeout(() => scanInputRef.current?.focus(), 200); }}
                  >
                    Scan Items
                  </Button>
                )}
              {/* Assign */}
              {isAdmin && (
                <Button
                  icon={<UserAddOutlined />}
                  onClick={() => setAssignModal(true)}
                >
                  Assign
                </Button>
              )}
            </Space>
          </Col>
        </Row>

        {/* Info Card */}
        <Card style={{ marginBottom: 16 }}>
          <Descriptions
            bordered
            size="small"
            column={{ xs: 1, sm: 2, md: 3 }}
          >
            <Descriptions.Item label="Sales Voucher">
              {jobDetail?.sale_id ? (
                <Link to={`/sales/quick-sales-voucher/${jobDetail.sale_id}`}>
                  {jobDetail?.voucher_no || jobDetail?.voucher_number || jobDetail.sale_id}
                </Link>
              ) : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Customer">
              {jobDetail?.customer_name || jobDetail?.customer || "-"}
              {jobDetail?.customer_phone && (
                <div style={{ fontSize: 12, color: "#888" }}>
                  {jobDetail.customer_phone}
                </div>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Customer Address" span={2}>
              {jobDetail?.customer_address ? (
                <Space>
                  <span>{jobDetail.customer_address}</span>
                  <Button
                    type="link"
                    size="small"
                    icon={<EnvironmentOutlined />}
                    style={{ padding: 0 }}
                    onClick={() =>
                      window.open(
                        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                          jobDetail.customer_address
                        )}`,
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                  >
                    View on Map
                  </Button>
                </Space>
              ) : (
                "-"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Delivery Type">
              {jobDetail?.delivery_type ? (
                <Tag color="blue">{jobDetail.delivery_type}</Tag>
              ) : (
                "-"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Delivery Date">
              {jobDetail?.delivery_date
                ? dayjs(jobDetail.delivery_date).format("DD MMM YYYY")
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Notes" span={3}>
              {jobDetail?.notes || "-"}
            </Descriptions.Item>
          </Descriptions>

          {/* Assigned users per step */}
          {jobDetail?.assignments && jobDetail.assignments.length > 0 && (
            <>
              <Divider orientation="left" orientationMargin={0} style={{ marginTop: 12 }}>
                Assignments
              </Divider>
              <Row gutter={[12, 8]}>
                {jobDetail.assignments.map((a) => (
                  <Col key={`${a.step}-${a.user_id}`} xs={24} sm={12} md={8}>
                    <Card
                      size="small"
                      style={{ background: "#fafafa" }}
                      bodyStyle={{ padding: "8px 12px" }}
                    >
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {a.step_label || a.step}
                      </Text>
                      <div style={{ fontWeight: 500 }}>{a.user_name}</div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </>
          )}
        </Card>

        {/* Items Table */}
        {jobDetail?.items && jobDetail.items.length > 0 && (
          <Card title="Items" style={{ marginBottom: 16 }}>
            <Table
              size="small"
              dataSource={jobDetail.items}
              columns={itemColumns}
              rowKey={(r, i) => r.sale_d_id || i}
              pagination={false}
            />
          </Card>
        )}

        {/* Timeline */}
        {timelineItems.length > 0 && (
          <Card title="Status History" style={{ marginBottom: 16 }}>
            <Timeline mode="left" items={timelineItems} />
          </Card>
        )}
      </Spin>

      {/* Mark Delivered Modal */}
      <Modal
        title="Mark as Delivered"
        open={deliverModal}
        onCancel={() => {
          setDeliverModal(false);
          deliverForm.resetFields();
        }}
        onOk={handleMarkDelivered}
        confirmLoading={actionLoading}
        okText="Mark Delivered"
      >
        <Form form={deliverForm} layout="vertical">
          <Form.Item name="notes" label="Delivery Notes">
            <Input.TextArea rows={3} placeholder="Add delivery notes..." />
          </Form.Item>
          <Form.Item name="photo_url" label="Photo URL (optional)">
            <Input placeholder="https://..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Cancel Modal */}
      <Modal
        title="Cancel Job"
        open={cancelModal}
        onCancel={() => {
          setCancelModal(false);
          cancelForm.resetFields();
        }}
        onOk={handleCancel}
        confirmLoading={actionLoading}
        okText="Cancel Job"
        okButtonProps={{ danger: true }}
      >
        <Form form={cancelForm} layout="vertical">
          <Form.Item
            name="notes"
            label="Reason for Cancellation"
            rules={[{ required: true, message: "Please provide a reason" }]}
          >
            <Input.TextArea rows={3} placeholder="Enter reason..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Assign Modal */}
      <Modal
        title="Assign User to Step"
        open={assignModal}
        onCancel={() => {
          setAssignModal(false);
          assignForm.resetFields();
        }}
        onOk={handleAssign}
        confirmLoading={actionLoading}
        okText="Assign"
      >
        <Form form={assignForm} layout="vertical">
          <Form.Item
            name="step"
            label="Step"
            rules={[{ required: true, message: "Select a step" }]}
          >
            <Select placeholder="Select step">
              {Object.entries(STATUS_LABELS).map(([key, label]) => (
                <Option key={key} value={key}>
                  {label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="user_id"
            label="Person"
            rules={[{ required: true, message: "Select a person" }]}
          >
            <Select
              showSearch
              placeholder="Select person"
              loading={usersLoading}
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? "").toLowerCase().includes(input.toLowerCase())
              }
            >
              {deliveryUsers.map((u) => (
                <Option key={u.id} value={u.id}>
                  {u.full_name}{u.roles ? ` (${u.roles})` : ""}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Scan Items Modal */}
      <Modal
        title={
          <span>
            <BarcodeOutlined style={{ marginRight: 8 }} />
            Scan Items — {scannedIds.size}/{jobDetail?.items?.length || 0} verified
          </span>
        }
        open={scanModal}
        onCancel={() => { setScanModal(false); setScanInput(""); }}
        footer={[
          <Button key="reset" onClick={() => setScannedIds(new Set())}>Reset</Button>,
          <Button key="close" type="primary" onClick={() => { setScanModal(false); setScanInput(""); }}>Done</Button>,
        ]}
        width={520}
      >
        <Input
          ref={scanInputRef}
          value={scanInput}
          onChange={(e) => setScanInput(e.target.value)}
          onPressEnter={() => handleScan(scanInput)}
          placeholder="Scan barcode or type item code / name, then press Enter"
          prefix={<BarcodeOutlined style={{ color: "#aaa" }} />}
          allowClear
          style={{ marginBottom: 16 }}
          autoFocus
        />
        <div style={{ maxHeight: 340, overflowY: "auto" }}>
          {(jobDetail?.items || []).map((it) => {
            const done = scannedIds.has(it.item_id);
            return (
              <div
                key={it.item_id || it.sale_d_id}
                onClick={() =>
                  setScannedIds((prev) => {
                    const next = new Set(prev);
                    done ? next.delete(it.item_id) : next.add(it.item_id);
                    return next;
                  })
                }
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 12px",
                  marginBottom: 6,
                  borderRadius: 6,
                  background: done ? "#f6ffed" : "#fafafa",
                  border: `1px solid ${done ? "#b7eb8f" : "#f0f0f0"}`,
                  cursor: "pointer",
                }}
              >
                <div>
                  <div style={{ fontWeight: done ? 600 : 400, color: done ? "#389e0d" : undefined }}>
                    {it.item_name}
                  </div>
                  {(it.item_code || it.item_barcode) && (
                    <div style={{ fontSize: 11, color: "#888" }}>
                      {[it.item_code, it.item_barcode].filter(Boolean).join(" · ")}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>×{it.item_qty}</Text>
                  {done
                    ? <CheckCircleFilled style={{ color: "#52c41a", fontSize: 18 }} />
                    : <div style={{ width: 18, height: 18, borderRadius: "50%", border: "2px solid #d9d9d9" }} />}
                </div>
              </div>
            );
          })}
        </div>
      </Modal>
    </div>
  );
};

export default DeliveryJobDetail;
